import React from 'react'
import Modal from '../../../shared/components/Modal'
import { useCart } from '../../cart/contexts/CartContext'
import { formatPrice } from '../../../shared/utils/checkoutHelpers'
import { buildImageUrl } from '../../../config'
import { Input } from '../../../shared/components'

interface OutOfStockModalProps {
  isOpen: boolean
  onClose: () => void
  outOfStockItem: { id: string; name: string } | null
}

export const OutOfStockModal: React.FC<OutOfStockModalProps> = ({
  isOpen,
  onClose,
  outOfStockItem
}) => {
  const { state: cartState, updateQuantity, removeItem, getCartItemId } = useCart()
  const [imageErrors, setImageErrors] = React.useState<Record<string, boolean>>({})

  if (!outOfStockItem) return null

  const cartItem = cartState.items.find(item => getCartItemId(item.product) === outOfStockItem.id)

  if (!cartItem) {
    // If for some reason the item is not in cart anymore
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Estoque Insuficiente"
      >
        <div className="text-center py-4">
          <p className="text-gray-600 mb-6">
            Ocorreu um erro ao processar o estoque de um dos itens. Por favor, revise seu carrinho.
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </Modal>
    )
  }

  const handleImageError = (productId: string) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }))
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) return
    const itemId = getCartItemId(cartItem.product)
    updateQuantity(itemId, newQuantity)
  }

  const handleRemoveItem = () => {
    const itemId = getCartItemId(cartItem.product)
    removeItem(itemId)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Estoque Insuficiente"
      size="sm"
    >
      <div className="space-y-6">
        <p className="text-gray-600">
          Infelizmente, não temos estoque suficiente para a quantidade solicitada do produto abaixo:
        </p>

        <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
          {/* Imagem */}
          <div className="flex-shrink-0 w-20 h-20 bg-white rounded-lg overflow-hidden border border-gray-200">
            {!imageErrors[cartItem.product.id] && cartItem.product.images && cartItem.product.images.length > 0 ? (
              <img
                src={
                  cartItem.product.images[0]?.startsWith('http')
                    ? cartItem.product.images[0]
                    : buildImageUrl(cartItem.product.images[0])!
                }
                alt={cartItem.product.name}
                onError={() => handleImageError(cartItem.product.id)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            )}
          </div>

          {/* Informações */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-gray-900 truncate">
              {cartItem.product.name}
            </h4>
            <p className="text-xs text-gray-500 mb-1">
              {cartItem.product.brand}
            </p>
            <p className="text-sm font-bold text-blue-600">
              {formatPrice(cartItem.product.price)}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajustar quantidade:
            </label>
            <div className="flex items-center">
              <button
                onClick={() => handleQuantityChange(cartItem.quantity - 1)}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-l-lg text-gray-600 hover:bg-gray-100"
              >
                -
              </button>
              <Input
                type="number"
                value={cartItem.quantity}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                className="w-16 h-10 text-center text-sm border-y border-x-0 border-gray-300 focus:outline-none"
                min="1"
              />
              <button
                onClick={() => handleQuantityChange(cartItem.quantity + 1)}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-r-lg text-gray-600 hover:bg-gray-100"
              >
                +
              </button>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <button
              onClick={onClose}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Tentar novamente com esta quantidade
            </button>
            <button
              onClick={handleRemoveItem}
              className="w-full bg-white text-red-600 py-3 px-4 rounded-lg font-semibold border border-red-200 hover:bg-red-50 transition-colors"
            >
              Remover item do carrinho
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
